<template>
  <div class="w-100">
    <ResendEmailModal ref="resend-email-modal"></ResendEmailModal>
    <div class="headline d-flex flex-row align-items-center justify-content-between w-100 border-bottom pb-3">
      <div class="d-flex align-items-center">
        <h2 class="card-title font-weight-bolder mr-5">{{ $t('title.overview') }}</h2>
        <div class="player-avatar-container">
          <img v-if="player" :src="player.avatarUrl" alt="avatar">
          <button @click="resetAvatar" class="btn btn-danger btn-xs">
            {{ $t('btn.reset_avatar') }}
          </button>
        </div>
      </div>

      <div class="d-flex" v-if="player">
        <DeletePlayer
            v-if="player.status !== 900"
            :player-id="player.id"
            :player-is-locked="player.status === 100"
            :player-has-founds="playerHasFounds"
            class="mr-1">
        </DeletePlayer>
        <b-button v-if="player.status !== 900" :disabled="player.status !== 10" @click="resendActivationEmail"
                  variant="primary" class="mr-1">
          {{ $t('btn.resend_activation_email') }}
        </b-button>
        <b-button v-if="player.status !== 900" :disabled="!passwordCanBeReset" @click="resendPasswordResetEmail"
                  variant="outline-primary">
          {{ $t('btn.reset_password') }}
        </b-button>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-4">
        <b-card
            no-body
            border-variant="secondary"
            :header="$t('title.personal_details')"
            header-bg-variant="secondary"
            header-text-variant="white"
            align="left"
        >
          <table class="table table-striped mb-0" v-if="player">
            <tbody>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.user_id') }}</td>
              <td>{{ player.id }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.first_name') }}</td>
              <td>{{ player.firstName }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.last_name') }}</td>
              <td>{{ player.lastName }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.nick_name') }}</td>
              <td>{{ player.nickName }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.gender') }}</td>
              <td>{{ $t($store.getters["root/sexLabel"](player.sex)) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.birth_date') }}</td>
              <td>{{ player.birthDate | dateFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.email') }}</td>
              <td>{{ player.email }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.phone') }}</td>
              <td>({{ player.phonePrefix }}) {{ player.phone }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.city') }}</td>
              <td>{{ player.city }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.street') }}</td>
              <td>{{ player.street }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.postal_code') }}</td>
              <td>{{ player.postCode }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.country') }}</td>
              <td>{{ $t($store.getters["root/countryLabel"](player.country)) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.language') }}</td>
              <td>{{ player.locale }}</td>
            </tr>
            </tbody>
          </table>
          <div class="spinner-wrapper" v-else>
            <b-spinner></b-spinner>
          </div>
        </b-card>
        <b-card
            no-body
            border-variant="secondary"
            :header="$t('title.last_note')"
            header-bg-variant="secondary"
            header-text-variant="white"
            align="left"
        >
          <b-card-body v-if="notes && notes.length">
            <div class="note__date" v-if="notes[0].createdAt">{{notes[0].createdAt}}</div>
            <div class="note__body" v-if="notes[0].content">
              {{notes[0].content}}
            </div>
          </b-card-body>

          <b-card-body v-else-if="notes && !notes.length">
            {{$t('notes.no.found')}}
          </b-card-body>

          <div class="spinner-wrapper" v-else>
            <b-spinner></b-spinner>
          </div>
        </b-card>
      </div>
      <div class="col-4">
        <b-card
            no-body
            border-variant="secondary"
            :header="$t('title.player_account')"
            header-bg-variant="secondary"
            header-text-variant="white"
            align="left"
        >
          <table class="table table-striped mb-0" v-if="player">
            <tbody>
            <tr>
              <td class="font-weight-bolder" style="width: 150px;">{{ $t('table.header.account_status') }}</td>
              <td>
                <div class="d-flex flex-row justify-content-between">
                  <div>
                    <b-badge pill variant="danger" v-if="player.status === 900">{{
                        $t('player_status_' + player.status)
                      }}
                    </b-badge>
                    <b-badge pill class="lock-player-status" v-if="player.status === 100">
                      {{ $t('player_status_' + player.status) }}
                    </b-badge>
                    <b-badge pill variant="success" v-if="player.status === 30">{{
                        $t('player_status_' + player.status)
                      }}
                    </b-badge>
                    <b-badge pill variant="primary" v-if="player.status === 20">{{
                        $t('player_status_' + player.status)
                      }}
                    </b-badge>
                    <b-badge pill variant="warning" v-if="player.status === 10">{{
                        $t('player_status_' + player.status)
                      }}
                    </b-badge>
                  </div>
                  <b-button v-if="player.status !== 900" @click="changeLockPlayer" size="xs"
                            :variant="player.status === 100 ? 'success' : 'danger'">
                    {{ player.status === 100 ? $t('btn.unlock') : $t('btn.lock') }}
                  </b-button>
                </div>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.registration_date') }}</td>
              <td>{{
                  player.registrationDate ? player.registrationDate : "no data" | dateFormat('YYYY-MM-DD HH:mm:ss')
                }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.registration_ip') }}</td>
              <td>{{ player.registrationIp ? player.registrationIp : "no data" }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.btag') }}</td>
              <td>{{ player.btag ? player.btag : "no data" }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.activation_date') }}</td>
              <td>{{
                  player.activationDate ? player.activationDate : "no data" | dateFormat('YYYY-MM-DD HH:mm:ss')
                }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.last_login_date') }}</td>
              <td>
                <div class="d-flex flex-row justify-content-between">
                  <span>{{ player.lastLoginDate | dateFormat('YYYY-MM-DD HH:mm:ss') }}</span>
                  <b-button @click="goToLoginHistory" variant="primary" size="xs">{{ $t('btn.show_more') }}</b-button>
                </div>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.last_login_ip') }}</td>
              <td>{{ player.lastLoginIp ? player.lastLoginIp : "no data" }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.is_allowed_to_login') }}</td>
              <td>
                <div class="d-flex flex-row justify-content-between">
                  <span>{{ player.isAllowedToLogin ? $t('label.yes') : $t('label.no') }}</span>
                  <b-button v-if="player && !player.isAllowedToLogin && player.status !== 900 && player.status !== 100"
                            @click="unlockAllowToLogin" variant="success" size="xs">{{ $t('btn.unlock') }}
                  </b-button>
                </div>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.kyc_status') }}</td>
              <td>
                <b-badge pill variant="warning" v-if="player.kycStatus === 'pending'">
                  {{ $t('kyc.label.status.' + player.kycStatus) }}
                </b-badge>
                <b-badge pill variant="danger" v-if="player.kycStatus === 'rejected'">
                  {{ $t('kyc.label.status.' + player.kycStatus) }}
                </b-badge>
                <b-badge pill variant="secondary" v-if="player.kycStatus === 'unverified'">
                  {{ $t('kyc.label.status.' + player.kycStatus) }}
                </b-badge>
                <b-badge pill variant="success" v-if="player.kycStatus === 'approved'">
                  {{ $t('kyc.label.status.' + player.kycStatus) }}
                </b-badge>
                <b-badge pill variant="info" v-if="player.kycStatus === 'requested'">
                  {{ $t('kyc.label.status.' + player.kycStatus) }}
                </b-badge>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.header.kyc_date') }}</td>
              <td>{{ player.kycUpdatedAt | dateFormat('YYYY-MM-DD HH:mm:ss') }}</td>
            </tr>
            </tbody>
          </table>
          <div class="spinner-wrapper" v-else>
            <b-spinner></b-spinner>
          </div>
        </b-card>
        <b-card
            no-body
            border-variant="secondary"
            :header="$t('title.bank_accounts')"
            header-bg-variant="secondary"
            header-text-variant="white"
            align="left"
        >
          <table class="table table-striped mb-0" v-if="player && playerBankAccounts">
            <tr>
              <th>{{ $t('table.header.bank_name') }}</th>
              <th>{{ $t('table.header.bank_number') }}</th>
              <th>{{ $t('table.header.bank_status') }}</th>
            </tr>
            <tbody>
            <tr v-for="bank in playerBankAccounts.items" :key="bank.id">
              <td class="font-weight-bolder">{{ bank.name }}</td>
              <td class="d-flex align-items-center" style="font-size: 12px;">{{ bank.prefix }}{{ bank.number }}</td>
              <td>
                <b-badge pill variant="success" v-if="bank.status === 'VERIFIED'">{{ $t('bank_status_' + bank.status) }}</b-badge>
                <b-badge pill variant="danger" v-if="bank.status !== 'VERIFIED'">{{ $t('bank_status_' + bank.status) }}</b-badge>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="spinner-wrapper" v-else>
            <b-spinner></b-spinner>
          </div>
        </b-card>

        <b-card
            no-body
            border-variant="primary"
            :header="$t('title.tags')"
            header-bg-variant="secondary"
            header-text-variant="white"
            align="left"
        >

          <table class="table table-striped mb-0">
            <tbody>
            <tr>
              <td colspan="2">
                <div>
                  <multiselect
                      v-model="selectedTags"
                      :options="selectTagOptions"
                      :multiple="true"
                      :close-on-select="false"
                      :clear-on-select="false"
                      :preserve-search="true"
                      placeholder="Add tag"
                      label="name"
                      track-by="name"
                      @input="tagChanged"
                  >
                  </multiselect>
                  <b-button @click="saveTags" class="m-1" size="sm" :variant="'success'" v-if="tagButtonsEnabled">
                    {{ $t('btn.save') }}
                  </b-button>
                  <b-button @click="resetTags" class="m-1" size="sm" :variant="'danger'" v-if="tagButtonsEnabled">
                    {{ $t('btn.cancel') }}
                  </b-button>

                </div>
                <div class="overlay tag-loading" v-if="tagsLoading">
                  <b-spinner></b-spinner>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </b-card>
      </div>
      <div class="col-4">
        <b-card
            no-body
            border-variant="quiz-coins"
            :header="$t(`WALLET_${wallets && wallets.length ? wallets[1].symbol.symbol : 'NO_WALLET'}`)"
            header-bg-variant="quiz-coins"
            header-text-variant="white"
            align="left"
        >
          <table class="table table-striped mb-0" v-if="player && wallets">
            <tbody>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.wallet_id') }}</td>
              <td>{{ wallets[1].id }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.wallet_currency') }}</td>
              <td>{{ $t(wallets[1].symbol.symbol) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.total_balance') }}</td>
              <td>{{ wallets[1].realMoneyBalanceAmount.amount | numberFormat }}
                <b-button @click="addCoins('QUIZ_COINS')" size="xs" class="float-right" variant="qc">+
                  {{ $t('player.wallet.add.qc.grid.btn') }}
                </b-button>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.locked_funds') }}</td>
              <td>{{ wallets[1].realMoneyLockedFoundsAmount.amount | numberFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.available_to_withdraw') }}</td>
              <td>-</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.bonuses') }}</td>
              <td>-</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.bonus_money_balance_amount') }}</td>
              <td>{{ wallets[1].bonusMoneyBalanceAmount.amount | numberFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.bonus_money_locked_founds_amount') }}</td>
              <td>{{ wallets[1].bonusMoneyLockedFoundsAmount.amount | numberFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.add_bonus_manual') }}</td>
              <td>
                <b-button @click="addBonus(wallets[1].id, currencies.quizCoins)" size="xs" class="float-right" variant="primary">
                  {{ $t('player.add_qc_bonus.btn') }}
                </b-button>
              </td>
            </tr>
            </tbody>
          </table>
          <div v-if="wallets && wallets.length === 0" class="spinner-wrapper">
            <b-spinner></b-spinner>
          </div>
        </b-card>

        <b-card
            no-body
            border-variant="free-coins"
            :header="$t(`WALLET_${wallets && wallets.length ? wallets[0].symbol.symbol : 'NO_WALLET'}`)"
            header-bg-variant="free-coins"
            header-text-variant="white"
            align="left"
        >
          <table class="table table-striped mb-0" v-if="player && wallets">

            <tbody>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.wallet_id') }}</td>
              <td>{{ wallets[0].id }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.wallet_currency') }}</td>
              <td>{{ $t(wallets[0].symbol.symbol) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.total_balance') }}</td>
              <td>{{ wallets[0].realMoneyBalanceAmount.amount | numberFormat }}
                <b-button @click="addCoins('FUN_COINS')" size="xs" class="float-right" variant="fs">+
                  {{ $t('player.wallet.add.fs.grid.btn') }}
                </b-button>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.locked_funds') }}</td>
              <td>{{ wallets[0].realMoneyLockedFoundsAmount.amount | numberFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.available_to_withdraw') }}</td>
              <td>-</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.bonuses') }}</td>
              <td>-</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.bonus_money_balance_amount') }}</td>
              <td>{{ wallets[0].bonusMoneyBalanceAmount.amount | numberFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.bonus_money_locked_founds_amount') }}</td>
              <td>{{ wallets[0].bonusMoneyLockedFoundsAmount.amount | numberFormat }}</td>
            </tr>
            <tr>
              <td class="font-weight-bolder">{{ $t('table.label.add_bonus_manual') }}</td>
              <td>
                <b-button @click="addBonus(wallets[0].id, currencies.funCoins)" size="xs" class="float-right" variant="primary">
                  {{ $t('player.add_qc_bonus.btn') }}
                </b-button>
              </td>
            </tr>
            </tbody>
          </table>
          <div v-if="wallets && wallets.length === 0" class="spinner-wrapper">
            <b-spinner></b-spinner>
          </div>
        </b-card>
      </div>
    </div>
    <div class="row">
      <div class="w-100">
        <div class="row mt-3">
          <div class="col-8">
            <div class="table-responsive">
              <b-card
                  no-body
                  border-variant="secondary"
                  :header="$t('title.agreements')"
                  header-bg-variant="secondary"
                  header-text-variant="white"
                  align="left"
              >
                <table class="table table-striped mb-0" v-if="player">

                  <tbody>
                  <tr>
                    <td>{{ $t('register.box.agreement1') }}</td>
                    <td><span class="badge badge-success" :class="{ 'badge badge-danger': !player.agreement1 }">{{
                        player.agreement1 ? '' + $t('label.yes') + '' : $t('label.no')
                      }}</span></td>
                    <td>{{ (player.agreement1ChangedAt ? player.agreement1ChangedAt : player.registrationDate)| dateFormat('YYYY-MM-DD HH:mm:ss')}}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>{{ $t('register.box.agreement2') }}</td>
                    <td><span class="badge badge-success" :class="{ 'badge badge-danger': !player.agreement2 }">{{
                        player.agreement2 ? '' + $t('label.yes') + '' : $t('label.no')
                      }}</span></td>
                    <td>{{ (player.agreement2ChangedAt ? player.agreement2ChangedAt : player.registrationDate)| dateFormat('YYYY-MM-DD HH:mm')}}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>{{ $t('register.box.agreement3') }}</td>
                    <td><span class="badge badge-success" :class="{ 'badge badge-danger': !player.agreement3 }">{{
                        player.agreement3 ? '' + $t('label.yes') + '' : $t('label.no')
                      }}</span></td>
                    <td>
                      {{ (player.agreement3ChangedAt ? player.agreement3ChangedAt : player.registrationDate)| dateFormat('YYYY-MM-DD HH:mm')}}
                    </td>
                    <td>
                      <b-button
                          @click="changeAgreement('agreement3', player.agreement3)" variant="primary" size="xs">{{ $t('btn.change_agreement') }}
                      </b-button>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('register.box.agreement4') }}</td>
                    <td><span class="badge badge-success" :class="{ 'badge badge-danger': !player.agreement4 }">{{
                        player.agreement4 ? '' + $t('label.yes') + '' : $t('label.no')
                      }}</span></td>
                    <td>{{ (player.agreement4ChangedAt ? player.agreement4ChangedAt : player.registrationDate)| dateFormat('YYYY-MM-DD HH:mm')}}</td>
                    <td>
                      <b-button
                          @click="changeAgreement('agreement4', player.agreement4)" variant="primary" size="xs">{{ $t('btn.change_agreement') }}
                      </b-button>
                    </td>
                  </tr>
                  </tbody>
                </table>
                <div class="spinner-wrapper" v-else>
                  <b-spinner></b-spinner>
                </div>
              </b-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import ResendEmailModal from "./ResendEmailModal";
import DeletePlayer from "./DeletePlayer";
import Vue from "vue";
import AddCoinsModal from "./AddCoinsModal";
import AddBonusManualModal from "./AddBonusManualModal";
import Multiselect from "vue-multiselect";
import {mapState} from "vuex";
import alertify from "alertifyjs";

export default {
  components: {ResendEmailModal, DeletePlayer, Multiselect},
  data() {
    return {
      configuration: false,
      wallets: null,
      notes: null,
      playerHasFounds: null,
      selectedTags: null,
      tagButtonsEnabled: false,
      tagsLoading: true,
      currencies: {
        funCoins: 'FUN_COINS',
        quizCoins: 'QUIZ_COINS'
      },
      agreements: null
    }
  },
  computed: {
    ...mapState({
      bonuses: (state) => state.players.bonuses
    }),
    manualBonuses: function () {
      return this.bonuses.filter(bonus => bonus.type === 'MANUAL' && bonus.triggers.includes('NONE'));
    },
    apiKey: function () {
      return this.$store.getters["root/apiKey"];
    },
    player: function () {
      return this.$store.getters["players/player"];
    },
    playerWallet: function () {
      return this.$store.getters["players/player"];
    },
    playerBankAccounts: function () {
      return this.$store.getters['players/playerBankAccounts'];
    },
    passwordCanBeReset: function () {
      if (this.player) {
        return this.player.status !== 10 && this.player.status !== 100 && this.player.status !== 900;
      }
      return null;
    },
    selectTagOptions: function () {
      return this.$store.getters["root/tag"];
    },
    getSelectedTags: function () {
      return this.$store.getters["root/resourceTag"];
    },
  },
  created() {
    this.playerId = this.$route.params.id;
  },
  mounted() {
    this.setupTags();
    this.getPlayer();
    this.getBonuses();
  },
  watch: {
    $route() {
      // this.getPlayer();
    },
    player: function (newValue) {

      // eslint-disable-next-line no-prototype-builtins
      if (newValue && newValue.hasOwnProperty('wallets') && newValue.wallets.length) {
        this.checkIfPlayerHasFounds();
        this.wallets = newValue.wallets;
      }
    },
    getSelectedTags: function (newValue) {
      this.tagsLoading = false;
      this.selectedTags = newValue;
    }
  },
  methods: {
    setupTags() {
      this.tagsLoading = true;
      this.$store.dispatch('root/getTags', this.playerId);
      this.$store.dispatch('root/getResourceTags', this.playerId);
    },
    resetTags() {
      this.tagsLoading = true;
      this.$store.dispatch('root/getResourceTags', this.playerId)
          .then(() => {
            this.tagButtonsEnabled = false;
          })
          .catch(() => {
            this.tagButtonsEnabled = true;
          });

    },
    tagChanged() {
      this.tagButtonsEnabled = true;
    },
    saveTags() {
      this.tagsLoading = true;

      let tagsToSend = {tagIds: []};

      this.selectedTags.forEach(function(item){ tagsToSend.tagIds.push(item.id) });

      this.$store.dispatch('root/postTags', {tags: tagsToSend, referenceId:this.playerId})
          .then(() => {
            this.tagButtonsEnabled = false;
            this.tagsLoading = false;
          });

    },
    getNotes() {
      this.$store.dispatch('players/getPlayerNotes', this.$route.params.id).then(
          data => this.notes = data.items
      );
    },
    addCoins(type) {
      const
          ModalConstructor = Vue.extend(AddCoinsModal),
          modal = new ModalConstructor({parent: this});
      modal.form.currency = type;

      if (type === 'FUN_COINS') {
        modal.title = 'player.wallet.add.fs.title';
      } else {
        modal.title = 'player.wallet.add.qc.title';
      }


      modal.$mount();
    },
    addBonus(walletId, currency) {
      const filteredBonusesByCurrencyAndStatus = this.manualBonuses.filter(bonus => {
        return bonus.currency === currency && bonus.status !== 'INACTIVE';
      });

      const
          ModalConstructor = Vue.extend(AddBonusManualModal),
          modal = new ModalConstructor({parent: this, propsData: { walletId, bonuses: filteredBonusesByCurrencyAndStatus }});

      modal.$mount();
    },
    getPlayer() {
      this.$store.dispatch('players/getPlayer', this.playerId).then(resp => {
        this.agreements = {
          agreement3: resp.agreement3,
          agreement4: resp.agreement4
        };
      });
      this.$store.dispatch('players/getPlayerWallet', this.playerId);
      this.$store.dispatch('players/getPlayerBankAccounts', this.playerId);
      this.getNotes();
    },
    getBonuses() {
      this.$store.dispatch('players/getBonuses');
    },
    getMorePlayerInfo() {
      this.$store.dispatch('players/getPlayer', this.playerId);
    },
    showDeletePlayerModal() {
      this.$refs['delete-player-modal'][0].showModal();
    },
    resendActivationEmail() {
      const modalType = 'activationEmail';

      this.$store.dispatch('players/resendActivationEmail', this.player.id)
          .then(() => {
            this.$refs['resend-email-modal'].showModal(modalType);
          })
          .catch(() => {
            this.$refs['resend-email-modal'].showModal(modalType);
          });
    },
    resendPasswordResetEmail() {
      const modalType = 'resetPassword';

      this.$store.dispatch('players/resendPasswordResetEmail', this.player.id)
          .then(() => {
            this.$refs['resend-email-modal'].showModal(modalType);
          })
          .catch(() => {
            this.$refs['resend-email-modal'].showModal(modalType);
          });
    },
    changeLockPlayer() {
      if (this.player.status !== 100) {
        this.$store.dispatch('players/lockPlayer', this.player.id)
            .then(() => {
              this.getPlayer();
            });
      } else {
        this.$store.dispatch('players/unlockPlayer', this.player.id)
            .then(() => {
              this.getPlayer();
            });
      }
    },
    unlockAllowToLogin() {
      this.$store.dispatch('players/unlockAllowToLogin', this.player.id)
          .then(() => {
            this.getPlayer();
          });
    },
    goToLoginHistory() {
      this.$router.push({name: 'player-login-history', params: {id: this.playerId}});
    },
    checkIfPlayerHasFounds() {
      this.playerHasFounds = false;
    },
    changeAgreement(agreementName, agreementValue) {
      this.agreements[agreementName] = !agreementValue;

      const payload = {
        playerId: this.playerId,
        agreements: this.agreements
      }

      this.$store.dispatch('players/changeAgreement', payload)
      .then(() => {
        this.getPlayer();
      });
    },
    resetAvatar() {
      this.$bvModal.msgBoxConfirm(this.$t('reset_avatar.modal.text'), {
        title: this.$t('reset_avatar.modal.title'),
        okTitle: this.$t('reset_avatar.modal.confirm'),
        cancelTitle: this.$t('reset_avatar.modal.cancel'),
        okVariant: 'danger'
      })
      .then(value => {
        if(value) {
          this.$store.dispatch('players/resetAvatar', this.playerId)
            .then(() => {
              this.getPlayer();
              alertify.success(this.$t('alert.success'));
            }).catch(() => {
              alertify.error(this.$t('alert.error'));
            }
          );
        }
      });
    }
  }
}
</script>
